import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { RightOutlined, SearchOutlined } from '@ant-design/icons';
import { partenairesService } from '../../common/services';
import {
    Spin, Descriptions, Table, Tag, Typography, Card, Breadcrumb, Tabs,
    DatePicker, Select, Input, Space, Row, Col, Grid,
    Progress
} from 'antd';
import dayjs from 'dayjs';

const { Title } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const DetailPartenaire = () => {
    const { id } = useParams();

    // ---- Chargement / données de base ----
    const [partenaire, setPartenaire] = useState(null);
    const [loading, setLoading] = useState(true);

    // ---- Opérations (serveur) ----
    const [opsItems, setOpsItems] = useState([]);
    const [opsTotal, setOpsTotal] = useState(0);
    const [opsPage, setOpsPage] = useState(1);
    const [opsLimit, setOpsLimit] = useState(10);
    const [opsSort, setOpsSort] = useState('-date'); // 'date' | '-date'
    const [opsLoading, setOpsLoading] = useState(false);

    // Filtres opérations
    const [opsTypes, setOpsTypes] = useState([]);              // ['sos_credit','transfert','vehicule_modifie', ...]
    const [opsDateRange, setOpsDateRange] = useState(null);    // [startDayjs, endDayjs]
    const [opsQuery, setOpsQuery] = useState('');

    // ---- Historique de points (client) ----
    const [pointHistory, setPointHistory] = useState([]);
    const [phDateRange, setPhDateRange] = useState(null);
    const [phType, setPhType] = useState(undefined);
    const [phQuery, setPhQuery] = useState('');

    const [opsSum, setOpsSum] = useState(0);

    const [phPage, setPhPage] = useState(1);
    const [phLimit, setPhLimit] = useState(5);

    // Breakpoints & tailles adaptatives
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;                // < md
    const pad = isMobile ? 12 : 24;              // padding global
    const controlSize = isMobile ? 'small' : 'middle';
    const cardSize = isMobile ? 'small' : 'default';
    const crumbFS = isMobile ? 14 : 16;          // Breadcrumb font-size
    const titleLevel = isMobile ? 4 : 3;         // Title niveau
    const descColsDriver = isMobile ? 1 : 2;     // Descriptions => colonnes

    const GOAL_55 = 55;

    const bonus55Start = partenaire?.bonus55?.countingFrom ? dayjs(partenaire.bonus55.countingFrom) : null;
    const bonus55AwardedAt = partenaire?.bonus55?.awardedAt ? dayjs(partenaire.bonus55.awardedAt) : null;
    const bonus55Awarded = !!bonus55AwardedAt;

    const coursesSinceStart = Number(partenaire?.statistiquesCourses?.total ?? 0);
    const progress55 = Math.min(100, Math.round((coursesSinceStart / GOAL_55) * 100));

    const bonus55NotStarted = bonus55Start && bonus55Start.isAfter(dayjs()); // si la date de départ est dans le futur





    // ✅ useMemo avant toute condition/return
    const filteredPointHistory = useMemo(() => {
        return (pointHistory || []).filter((e) => {
            const d = dayjs(e.date);

            // date inclusive
            const okDate = !phDateRange || (
                d.valueOf() >= phDateRange[0].startOf('day').valueOf() &&
                d.valueOf() <= phDateRange[1].endOf('day').valueOf()
            );

            const okType = !phType || e.type === phType;

            const q = (phQuery || '').trim().toLowerCase();
            const okQ = !q || (e.description?.toLowerCase().includes(q) || e.phone?.toLowerCase().includes(q));

            return okDate && okType && okQ;
        });
    }, [pointHistory, phDateRange, phType, phQuery]);

    const phTotalPoints = useMemo(() => {
        return (filteredPointHistory || []).reduce((acc, e) => {
            const p = Number(e.points) || 0;
            return acc + p;
        }, 0);
    }, [filteredPointHistory]);


    // ---- 1er chargement ----
    useEffect(() => {
        if (!id) return;
        setLoading(true);
        partenairesService.getById(id)
            .then((data) => {
                setPartenaire(data);
                const ops = data?.operations || {};
                setOpsItems(ops.items || []);
                setOpsTotal(ops.total || 0);
                setPointHistory(ops.pointHistory || []);
            })
            .catch((err) => console.error('Erreur chargement partenaire :', err))
            .finally(() => setLoading(false));
    }, [id]);

    // ---- Fetch opérations avec filtres/pagination/tri ----
    const fetchOperations = async () => {
        if (!id) return;
        try {
            setOpsLoading(true);
            const params = {
                page: opsPage,
                limit: opsLimit,
                sort: opsSort,
                q: opsQuery || undefined,
                types: opsTypes.length ? opsTypes : undefined,
                start: opsDateRange?.[0]?.format('YYYY-MM-DD'),
                end: opsDateRange?.[1]?.format('YYYY-MM-DD'),
            };
            // Assure-toi d’avoir partenairesService.getByIdWithQuery côté service
            const data = await partenairesService.getByIdWithQuery(id, params);
            setPartenaire((prev) => ({ ...(prev || {}), ...data }));
            const ops = data?.operations || {};
            setOpsItems(ops.items || []);
            setOpsTotal(ops.total || 0);
            setOpsSum(ops.sumAmount || 0); // 👈 somme serveur selon filtres
            if (ops.pointHistory) setPointHistory(ops.pointHistory);

            // const data = await partenairesService.getByIdWithQuery(id, params);
            // setPartenaire((prev) => ({ ...(prev || {}), ...data }));
            // const ops = data?.operations || {};
            // setOpsItems(ops.items || []);
            // setOpsTotal(ops.total || 0);
            // if (ops.pointHistory) setPointHistory(ops.pointHistory);
        } catch (e) {
            console.error('Erreur fetchOperations:', e);
        } finally {
            setOpsLoading(false);
        }
    };

    // Rafraîchir quand filtres changent
    useEffect(() => {
        if (!partenaire) return;
        fetchOperations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opsPage, opsLimit, opsSort, opsTypes, opsDateRange, opsQuery]);

    useEffect(() => {
        setPhPage(1);
    }, [phDateRange, phType, phQuery]);


    const formatPhoneNumber = (phone) => {
        if (!phone) return '';
        let local = phone.replace('+225', '').trim();
        if (!local.startsWith('0')) local = '' + local;
        return local.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
    };

    // ---- Colonnes ----
    const columnsVehicules = [
        { title: 'Marque', dataIndex: 'marque' },
        { title: 'Modèle', dataIndex: 'modele' },
        { title: 'Immatriculation', dataIndex: 'immatriculation' },
        { title: 'Année', dataIndex: 'annee' },
        { title: 'Couleur', dataIndex: 'couleur' },
        {
            title: 'Catégorie',
            dataIndex: ['categorieServiceId', 'name'],
            render: (name) => <Tag color="blue">{name}</Tag>,
        },
        {
            title: 'Classe service',
            dataIndex: 'categories',
            render: (types) => (types || []).map((cat, idx) => <Tag key={idx} color="green">{cat}</Tag>),
        },
    ];

    const TYPE_LABEL = {
        sos_credit: 'SOS Crédit',
        transfert: 'Transfert',
        vehicule_modifie: 'Changement véhicule',
        promo_dimanche: 'Promo dimanche',
        changement_numero: 'Changement n°',
    };
    const TYPE_COLOR = {
        sos_credit: 'purple',
        transfert: 'geekblue',
        vehicule_modifie: 'volcano',
        promo_dimanche: 'gold',
        changement_numero: 'cyan',
    };

    const columnsOperations = [
        {
            title: 'Type',
            dataIndex: 'type',
            render: (t) => <Tag color={TYPE_COLOR[t] || 'default'}>{TYPE_LABEL[t] || t}</Tag>,
        },
        {
            title: 'Détails',
            render: (_, r) => {
                if (r.type === 'transfert') {
                    return (
                        <span>
                            {formatPhoneNumber(r.senderPhone)} <RightOutlined /> {formatPhoneNumber(r.receiverPhone)}
                        </span>
                    );
                }
                if (r.type === 'vehicule_modifie') {
                    return (
                        <span>
                            {r.meta?.ancienneImmatriculation} <RightOutlined /> {r.meta?.nouvelleImmatriculation}
                        </span>
                    );
                }
                if (r.type === 'changement_numero') {
                    return (
                        <span>
                            {formatPhoneNumber(r.meta?.ancienNumero)} <RightOutlined /> {formatPhoneNumber(r.meta?.nouveauNumero)}
                        </span>
                    );
                }
                if (r.type === 'promo_dimanche') {
                    return 'Activation règle dimanche';
                }
                // sos_credit
                return formatPhoneNumber(r.phone);
            },
        },
        {
            title: 'Montant',
            dataIndex: 'amount',
            render: (a) => (a ? `${a} FCFA` : '—'),
        },
        {
            title: 'Date',
            dataIndex: 'date',
            sorter: true, // on mappe vers sort 'date' / '-date'
            render: (d) => dayjs(d).format('DD/MM/YYYY HH:mm'),
        },
    ];

    const onOpsTableChange = (pagination, _filters, sorter) => {
        setOpsPage(pagination.current || 1);
        setOpsLimit(pagination.pageSize || 10);

        if (sorter?.field === 'date') {
            setOpsSort(sorter.order === 'ascend' ? 'date' : '-date');
        }
    };

    if (loading || !partenaire) {
        return (
            <div style={{ padding: 40, textAlign: 'center' }}>
                <Spin tip="Chargement des détails..." size="large" />
            </div>
        );
    }

    const format2 = (v) => (v === null || v === undefined || v === '' || isNaN(Number(v))) ? '—' : Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return (
        <div style={{ padding: pad }}>
            <Breadcrumb
                separator={<RightOutlined style={{ fontSize: crumbFS, color: '#999', fontWeight: 'bolder' }} />}
                style={{ marginBottom: isMobile ? 12 : 16 }}
            >
                <Breadcrumb.Item>
                    <Link to="/dashboard" style={{ fontSize: crumbFS, fontWeight: 'bolder' }}>🏠 ACCUEIL</Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <Link to="/dashboard/partenaires" style={{ fontSize: crumbFS, fontWeight: 'bolder' }}>👥 TIGOON</Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <span style={{ fontSize: crumbFS, fontWeight: 'bolder' }}>🔍 DETAIL</span>
                </Breadcrumb.Item>
            </Breadcrumb>

            <Card bordered size={cardSize}>
                <Title level={titleLevel} style={{ marginBottom: isMobile ? 12 : 16 }}>Détails TIGOON</Title>

                <Tabs
                    defaultActiveKey="1"
                    size={controlSize}
                    tabBarGutter={isMobile ? 8 : 16}
                    tabPosition="top"
                    destroyInactiveTabPane
                >
                    {/* 👤 Driver */}
                    <TabPane tab="👤 Driver" key="1">
                        <Descriptions
                            bordered
                            column={descColsDriver}   // ou: column={{ xs:1, sm:1, md:2 }}
                            size={controlSize}
                            labelStyle={{ width: isMobile ? 140 : 180 }}
                        >
                            <Descriptions.Item label="Nom">
                                {partenaire.firstName} {partenaire.lastName}
                            </Descriptions.Item>
                            <Descriptions.Item label="Téléphone">
                                {formatPhoneNumber(partenaire.phone)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Permis de conduire">
                                {partenaire.numeroPermisConduire}
                            </Descriptions.Item>
                            <Descriptions.Item label="Identifiant">
                                {partenaire.id}
                            </Descriptions.Item>
                            <Descriptions.Item label="Créé le">
                                {new Date(partenaire.createdAt).toLocaleString()}
                            </Descriptions.Item>
                            <Descriptions.Item label="Mis à jour le">
                                {new Date(partenaire.updatedAt).toLocaleString()}
                            </Descriptions.Item>
                        </Descriptions>
                    </TabPane>

                    {/* 🚗 Véhicules */}
                    <TabPane tab="🚗 Véhicules" key="2">
                        <Table
                            size={controlSize}
                            columns={columnsVehicules}
                            dataSource={partenaire.vehicules}
                            rowKey="id"
                            pagination={{
                                pageSize: 5,
                                showSizeChanger: !isMobile,
                                showLessItems: isMobile,
                            }}
                            scroll={{ x: true, y: isMobile ? 320 : undefined }}
                            sticky
                        />
                    </TabPane>

                    {/* 💰 Revenu */}
                    <TabPane tab="💰 Revenu" key="3">
                        <Descriptions
                            bordered
                            column={1}
                            size={controlSize}
                            title="Détail du revenu"
                            labelStyle={{ width: isMobile ? 160 : 220 }}
                        >
                            <Descriptions.Item label="Points disponibles">
                                {Number(partenaire?.soldePoints?.point ?? 0).toFixed(2)} pts
                            </Descriptions.Item>
                            <Descriptions.Item label="Date dernière mise à jour">
                                {partenaire?.soldePoints?.updatedAt
                                    ? new Date(partenaire.soldePoints.updatedAt).toLocaleString()
                                    : 'Non défini'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Mois dernière mise à jour">
                                {partenaire?.soldePoints?.moisDerniereMiseAJour ?? 'Non défini'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Bonus 130h attribué">
                                {partenaire?.bonusCentTrenteHeuresAttribue ?? 'Non'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Bonus Lundis">
                                {partenaire?.bonusLundis ? 'Oui' : 'Non'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Nombre d’heures ce mois-ci">
                                {partenaire?.statistiquesHeuresMensuelles?.heures ?? 0} heures
                            </Descriptions.Item>
                            <Descriptions.Item label="Bonus 55e course">
                                {bonus55Awarded ? (
                                    <Tag color="green">
                                    Attribué le {bonus55AwardedAt?.format('DD/MM/YYYY HH:mm')}
                                    </Tag>
                                ) : bonus55NotStarted ? (
                                    <Tag color="default">
                                    À venir — début le {bonus55Start?.format('DD/MM/YYYY')}
                                    </Tag>
                                ) : (
                                    <Tag color="orange">En cours</Tag>
                                )}

                                {/* Progression */}
                                {!bonus55Awarded && !bonus55NotStarted && (
                                    <div style={{ marginTop: 8 }}>
                                    <Progress
                                        percent={progress55}
                                        size="small"
                                        status="active"
                                        format={() => `${coursesSinceStart}/${GOAL_55} courses`}
                                    />
                                    {bonus55Start && (
                                        <div style={{ marginTop: 4, color: '#888' }}>
                                        Comptage démarré le {bonus55Start.format('DD/MM/YYYY')}
                                        </div>
                                    )}
                                    </div>
                                )}

                                {/* Si déjà attribué, on rappelle l'info de départ */}
                                {bonus55Awarded && bonus55Start && (
                                    <div style={{ marginTop: 4, color: '#888' }}>
                                    Comptage démarré le {bonus55Start.format('DD/MM/YYYY')}
                                    </div>
                                )}
                                </Descriptions.Item>

                                <Descriptions.Item label="Courses (depuis début bonus 55)">
                                {coursesSinceStart} course{coursesSinceStart > 1 ? 's' : ''}
                                </Descriptions.Item>

                        </Descriptions>
                    </TabPane>

                    {/* ⚙️ Opérations */}
                    <TabPane tab="⚙️ Opérations" key="4">
                        <Space direction="vertical" style={{ width: '100%', marginBottom: isMobile ? 8 : 12 }} size={isMobile ? 8 : 12}>
                            <Row gutter={[12, 12]}>
                                <Col xs={24} md={10}>
                                    <RangePicker
                                        size={controlSize}
                                        style={{ width: '100%' }}
                                        format="DD/MM/YYYY"
                                        value={opsDateRange}
                                        onChange={(range) => { setOpsDateRange(range); setOpsPage(1); }}
                                        placeholder={['Date début', 'Date fin']}
                                    />
                                </Col>
                                <Col xs={24} md={8}>
                                    <Select
                                        size={controlSize}
                                        mode="multiple"
                                        allowClear
                                        style={{ width: '100%' }}
                                        placeholder="Catégories"
                                        value={opsTypes}
                                        onChange={(vals) => { setOpsTypes(vals); setOpsPage(1); }}
                                        options={[
                                            { value: 'sos_credit', label: 'SOS Crédit' },
                                            { value: 'transfert', label: 'Transfert' },
                                            { value: 'vehicule_modifie', label: 'Changement véhicule' },
                                            { value: 'promo_dimanche', label: 'Promo dimanche' },
                                            { value: 'changement_numero', label: 'Changement n°' },
                                        ]}
                                    />
                                </Col>
                                <Col xs={24} md={6}>
                                    <Input
                                        size={controlSize}
                                        allowClear
                                        prefix={<SearchOutlined />}
                                        placeholder="Recherche (téléphone, id, immat...)"
                                        value={opsQuery}
                                        onChange={(e) => { setOpsQuery(e.target.value); setOpsPage(1); }}
                                    />
                                </Col>
                            </Row>

                            <Table
                                size={controlSize}
                                loading={opsLoading}
                                columns={columnsOperations}
                                dataSource={opsItems}
                                rowKey="_id"
                                pagination={{
                                    current: opsPage,
                                    pageSize: opsLimit,
                                    total: opsTotal,
                                    showSizeChanger: true,
                                    pageSizeOptions: [5, 10, 20, 50],
                                    showLessItems: isMobile,
                                }}
                                onChange={onOpsTableChange}
                                locale={{ emptyText: 'Aucune opération' }}
                                scroll={{ x: true, y: isMobile ? 360 : undefined }}
                                sticky
                            />

                            <Row justify={isMobile ? 'center' : 'end'}>
                                <Col>
                                    <Typography.Text strong style={{ fontSize: isMobile ? 13 : 14 }}>
                                        Somme des montants (après filtres)&nbsp;:
                                        {' '}
                                        {Number(opsSum || 0).toLocaleString('fr-FR')} FCFA
                                    </Typography.Text>
                                </Col>
                            </Row>
                        </Space>
                    </TabPane>

                    {/* 📜 Historique du compte */}
                    <TabPane tab="📜 Historique du compte" key="5">
                        <Space direction="vertical" style={{ width: '100%', marginBottom: isMobile ? 8 : 12 }} size={isMobile ? 8 : 12}>
                            <Row gutter={[12, 12]}>
                                <Col xs={24} md={10}>
                                    <RangePicker
                                        size={controlSize}
                                        style={{ width: '100%' }}
                                        format="DD/MM/YYYY"
                                        value={phDateRange}
                                        onChange={setPhDateRange}
                                        placeholder={['Date début', 'Date fin']}
                                    />
                                </Col>
                                <Col xs={24} md={6}>
                                    <Select
                                        size={controlSize}
                                        allowClear
                                        style={{ width: '100%' }}
                                        placeholder="Filtrer par type"
                                        value={phType}
                                        onChange={setPhType}
                                        options={[
                                            { value: 'points_rechargement', label: 'Points rechargement' },
                                            { value: 'sos_credit_montant', label: 'SOS crédit (montant)' },
                                            { value: 'sos_credit_frais', label: 'SOS crédit (frais)' },
                                            { value: 'bonus_dimanche', label: 'Bonus dimanche' },
                                            { value: 'transfert_uv', label: 'Transfert UV' },
                                            { value: 'maj_classe_service', label: 'Classe de service' },
                                            { value: 'lier_vehicule', label: 'Liaison véhicule au chauffeur' },
                                            { value: 'detacher_vehicule', label: 'Détacher véhicule au chauffeur' },
                                            { value: 'retrait_points_virement', label: 'Virement' },
                                            { value: 'bonus_130_heures', label: 'Bonus 130ieme Heure' },
                                            { value: 'bonus_55_courses', label: 'Bonus 55ieme Course' },
                                        ]}
                                    />
                                </Col>
                                <Col xs={24} md={8}>
                                    <Input
                                        size={controlSize}
                                        allowClear
                                        prefix={<SearchOutlined />}
                                        placeholder="Recherche (description, téléphone)"
                                        value={phQuery}
                                        onChange={(e) => setPhQuery(e.target.value)}
                                    />
                                </Col>
                            </Row>

                            <Table
                                size={controlSize}
                                columns={[
                                    { title: 'Type', dataIndex: 'type' },
                                    { title: 'Téléphone', dataIndex: 'phone', render: (p) => formatPhoneNumber(p) },
                                    { title: 'Description', dataIndex: 'description' },
                                    { title: 'Point Avant', dataIndex: 'soldeAvant', align: 'right', render: format2 },
                                    {
                                        title: 'Points',
                                        dataIndex: 'points',
                                        render: (points) => (
                                            <Tag color={points >= 0 ? 'green' : 'red'}>
                                                {points > 0 ? `+${points}` : points}
                                            </Tag>
                                        ),
                                    },
                                    { title: 'Point Après', dataIndex: 'soldeApres', align: 'right', render: format2 },
                                    { title: 'Date', dataIndex: 'date', render: (d) => dayjs(d).format('DD/MM/YYYY HH:mm') },
                                ]}
                                dataSource={filteredPointHistory}
                                rowKey="_id"
                                pagination={{
                                    current: phPage,
                                    pageSize: phLimit,
                                    total: filteredPointHistory.length,
                                    showSizeChanger: true,
                                    showLessItems: isMobile,
                                    pageSizeOptions: [5, 10, 20, 50],
                                    onChange: (page, pageSize) => {
                                        setPhPage(page);
                                        setPhLimit(pageSize);
                                    },
                                }}
                                locale={{ emptyText: 'Aucun historique de points' }}
                                scroll={{ x: true, y: isMobile ? 360 : undefined }}
                                sticky
                            />

                            <Row justify={isMobile ? 'center' : 'end'}>
                                <Col>
                                    <Typography.Text strong style={{ fontSize: isMobile ? 13 : 14 }}>
                                        Somme des points (après filtres)&nbsp;:
                                        {' '}
                                        {phTotalPoints.toLocaleString('fr-FR')} pts
                                    </Typography.Text>
                                </Col>
                            </Row>
                        </Space>
                    </TabPane>
                </Tabs>
            </Card>
        </div>

    );
};

export default DetailPartenaire;
